import * as angular from 'angular';
import * as moment from 'moment';
import * as d3 from 'd3';
import * as _ from 'lodash';

import {
    ActualDataType,
    ComponentDefinition,
    Group, 
    IDisplayedColumn,
    IIdentifierVm,
    IMappingDictionary,
    IValueViewModel,
    Identifier,
    IdentifierMiniaturesName,
    IdentifierType
} from '../../../../models';
import { IExportToCsvService, IIdentifierStateService, IStoreService } from '../../../../services';
import {  defaultItemPerPage, isEditMode, identifierSelected, identifierSaved, formDirtyChanged, Helper } from '../../../../common';
import { IRootScope } from '../../../../IRootScope';


class IdentifiersStateController implements angular.IComponentController {
    displayed: IIdentifierVm[];
    displayedIdentifiers: Identifier[];
    groupGuid: any;
    hasMore: boolean;
    isEditMode: boolean;
    isSelectionPrevented: boolean;
    menu: IDisplayedColumn[];
    pageNumber: number;
    selectedItem: Identifier;
    selectedItemCanDelete: boolean;
    tableForm: angular.IFormController;

    readonly canEdit: boolean;
    readonly contextualForm: angular.IFormController;
    readonly selectedGroup: Group;

    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        'storeService',
        'exportToCsvService',
        'identifierStateService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _storeService: IStoreService,
        private _exportToCsvService: IExportToCsvService,
        private _identifierStateService: IIdentifierStateService
    ) {
    }

    $onInit() {
        this.groupGuid = this.selectedGroup.guid;
        this.displayedIdentifiers = this.selectedGroup?.identifierCollection?.length > 0
            ? this.selectedGroup?.identifierCollection
            : [];

        this._garbages.push(this._$scope.$on(identifierSelected, ($event, value: Identifier) => {
            this.tryUpdateSelectedItem(value);
        }));

        this._$scope.$on(formDirtyChanged, (event, isDirty) => {
            this.isSelectionPrevented = isDirty;
        });

        this._garbages.push(this._$scope.$on(isEditMode, (event, data) => {
            if (this.tableForm && this.tableForm.$visible) {
                this.tableForm.$cancel();
                this.cancel();
            }
            this.isEditMode = data;

            if (!this.isEditMode) {
                if (this._identifierStateService.canSelectItem(null)) {
                    this.setSelectedItem(null);
                } else {
                    // TODO don't prevent closing of editMode
                    // TODO should we use a yes/no question to save ?
                    event.preventDefault();
                }
            }
        }));
        
        this._garbages.push(this._$scope.$watch(() => this.menu, (newValue, oldValue) => {
            if (!oldValue && newValue) {
                this.initiazePropertyMapper(newValue);
            }
        }));

        this._garbages.push(this._$scope.$watch(() => this.selectedItem, () => {
            this._identifierStateService.selectedItem(this.selectedItem);
            this.selectedItemCanDelete = this.canDeleteItem(this.selectedItem);
        }));

        this._garbages.push(this._$scope.$watch(() => this.pageNumber, (newValue) => {
            if (newValue) {
                this.hasMore = this.selectedGroup && (this.pageNumber * defaultItemPerPage)
                    < this.displayedIdentifiers.length;
            }
        }));

        this.isEditMode = this._$rootScope.isEditMode;
        this._$rootScope.$broadcast(isEditMode, this._$rootScope.isEditMode);
    }

    $onChanges(changesObj: angular.IOnChangesObject): void {
        if (changesObj['selectedGroup']?.currentValue) {
            if (this.selectedGroup.identifierCollection?.length > 0) {
                this.displayedIdentifiers = this.selectedGroup?.identifierCollection;
                this._identifierStateService.identifiers(this.displayedIdentifiers);
                const selected = _.find(this.displayedIdentifiers, i => i.guid === this.selectedItem?.guid);
                this.tryUpdateSelectedItem(selected);
            }
        }
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
        // clean creating identifier if leaving page
        this.displayedIdentifiers = this.displayedIdentifiers.filter(i => !i.isCreating);
    }

    less() {
        this.pageNumber--;
    }

    more() {
        this.pageNumber++;
    }

    cancel(): void {
        this.tableForm.$cancel();
        this.tableForm.$setPristine();
        if (this._identifierStateService.formController) {
            this._identifierStateService.formController.$rollbackViewValue();
        }
    }

    tryUpdateSelectedItem(selected): void {
        if (!this.contextualForm || this.contextualForm.$pristine) {
            this.setSelectedItem(selected);
        }
    }

    submit(): void {
        this.tableForm.$submit();
    }

    unselect($event: any) {
        const isPrevented = $event.isDefaultPrevented ? $event.isDefaultPrevented() : $event.defaultPrevented;
        if (!isPrevented && this.selectedItem !== null) {
            this._$rootScope.$broadcast(identifierSelected, null);
        }
    }

    drop(data) {
        const itemId: string = data.itemId;
        if (itemId) {
            const type = this.findIdentifierType(itemId);
            if (type !== false) {
                this.createNewIdentifier(type, itemId);
            }
        }
    }

    exportToCsv() {
        const fileName = `Identifiers_${this.selectedGroup.name}_${moment(new Date()).format('YYYY-MM-DD')}.csv`;
        const data = this._exportToCsvService.getCsvContent(this.menu, this.displayed, this._propertyMapper);
        this._exportToCsvService.getCsvFromData(data, fileName);
    }

    private _propertyMapper: IMappingDictionary<IValueViewModel & { linked?: IValueViewModel }>;

    private initiazePropertyMapper(exportedColumns: IDisplayedColumn[]) {
        this._propertyMapper = {};
        if (exportedColumns) {
            exportedColumns.forEach(m => this._propertyMapper[m.property] = v => v[m.property]);
        }
        this._propertyMapper['tag'] = v => v?.tagName ? v.tagName : v.linked ? v.linked.name : null;
        this._propertyMapper['tagName'] = this._propertyMapper['tag'];
        this._propertyMapper['type'] = v => v.variableTypeName;
        this._propertyMapper['subType'] = v => v.variableSubTypeName;
        this._propertyMapper['qualifier'] = v => v.qualifierName;
        this._propertyMapper['unit'] = v => v.unitName;
    }

    private canDeleteItem(item: any): boolean {
        let ret = item && item.canDelete;
        if (item && this.selectedGroup && this.selectedGroup.type && this.selectedGroup.type.toLowerCase() === 'redundancy') {
            // redundancy group always have itthis as precedent
            // we should allow to delete only if this is the only one
            ret = item.precedentGroupList && item.precedentGroupList.length === 1;
        }
        return ret;
    }

    private createNewIdentifier(type: IdentifierType, itemId: string) {
        const newIdentifier: Identifier = {
            isCreating: true,
            type: type,
            dataType: ActualDataType.Float,
            guid: Helper.getGuid(),
            sortingOrder: this.displayedIdentifiers.length > 0
                ? d3.max(this.displayedIdentifiers, i => i.sortingOrder) + 1
                : 0,
            selectionStrategy: type === IdentifierType.Target ? 1 : 0,
            aggregationMode: 0,
            customProperties: {},
            variableTypeGuid: '',
            resolution: 0,
            resolutionPercentage: 0
        } as Identifier;
        // Template for reconciliaton result / total penalty
        if (itemId.indexOf('recres') === 0) {
            newIdentifier.name = itemId.replace('recres', '');
            newIdentifier.variableTypeGuid = '09cb049e-b34c-4fc9-b82d-6cfdb598cdae';
            newIdentifier.unitGuid = 'c20b1098-db7a-49b6-b2ae-553dee5704f3';
            this._storeService.dispatchers().identifiers
                .add(newIdentifier, this.selectedGroup)
                .then(d => {
                    newIdentifier.isCreating = false;
                    this._storeService.dispatchers().valueViewModels.getAll(true);
                    this._$rootScope.$broadcast(identifierSaved, d);
                    this.displayedIdentifiers.push(d);
                    this.setSelectedItem(d);
                });
        } else {
            this.displayedIdentifiers.push(newIdentifier);
            this.setSelectedItem(newIdentifier);
        }
    }

    private findIdentifierType(itemId: string): (IdentifierType | false) {
        if (itemId === IdentifierMiniaturesName.Linked) {
            return IdentifierType.Linked;
        } else if (itemId === IdentifierMiniaturesName.Validated) {
            return IdentifierType.Validated;
        } else if (itemId === IdentifierMiniaturesName.Measured) {
            return IdentifierType.Tag;
        } else if (itemId === IdentifierMiniaturesName.Target) {
            return IdentifierType.Target;
        } else if (itemId === IdentifierMiniaturesName.Calculated) {
            return IdentifierType.CalculatedValue;
        } else if (itemId === IdentifierMiniaturesName.TotalPenalty) {
            return IdentifierType.CalculatedValue;
        } else if (itemId === IdentifierMiniaturesName.ReconciliationStatus) {
            return IdentifierType.CalculatedValue;
        }
        return false;
    }

    private setSelectedItem(selectedItem: Identifier) {
        this.selectedItem = selectedItem;
        this._identifierStateService.selectedItem(selectedItem);
    }
}

export const wtIdentifiersState: ComponentDefinition = {
    componentId: 'wtIdentifiersState',
    controller: IdentifiersStateController,
    template: require('./identifiersState.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedGroup: '<',
        selectedItem: '=',
        contextualForm: '<?',
        canEdit: '<'
    }
};